import React, {Fragment, memo} from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
const DisplayImage = dynamic(import('@/components/Ui-components/display-image'));
const CardContent = dynamic(import('@mui/material/CardContent'));
const CardHeader = dynamic(import('@mui/material/CardHeader'));
const Card = dynamic(import('@mui/material/Card'));
import parse from 'html-react-parser';
import {AnimatedComponent} from '../AnimatedComponent';
import {fadeInUp} from '@/animation/scroll-animation';
import {colors} from '@/styles/tokens';

// Migrated from globals.scss `.helper-section-header-link { a { color: #111111; } }` (compiled
// descendant selector `.helper-section-header-link a`). Sole owner confirmed — this className has
// exactly one consumer in the codebase: the two `recipe-card-heading` Typography elements below.
// Targets any `<a>` nested inside the parsed heading HTML (subData.heading may itself contain an
// anchor), coloring it the same near-black as the surrounding heading text rather than default link
// blue.
const helperSectionHeaderLinkSx = {
    '& a': {
        color: colors.text.primary // exact token match for #111111
    }
} as const;

// Migrated from solutions.scss `.helper-section-card-content { margin-block-end: 0 !important; }`.
// Sole owner confirmed — this className has exactly one consumer in the codebase: the `<p>` below.
const helperSectionCardContentSx = {
    marginBlockEnd: '0 !important'
} as const;

// Migrated from solutions.scss `.helper-card-content-wrapper`. Sole owner confirmed — this
// className has exactly one consumer in the codebase: the `CardContent` below.
const helperCardContentWrapperSx = {
    paddingBottom: '28px !important',
    paddingTop: '8px'
} as const;

const renderCard = (subData: any, className: any, classn: any, iconBoxSx: any) => {
    return (
        <Card variant="outlined" className={`recipe-review-card ${classn} ${className}`}>
            <CardHeader
                sx={{pt: 3.5}}
                avatar={
                    <Box className="helper-card-icon-box" sx={iconBoxSx}>
                        <DisplayImage
                            src={subData.icon}
                            alt={subData.heading}
                            className="helper-card-icon helper-card-icon-sizing"
                            priority={false}
                        />
                    </Box>
                }
            />
            {subData.redirect !== '' ? (
                <Link href={subData?.redirect} target="_blank" className="services_link menu-links">
                    <Typography
                        variant="h3"
                        component="h3"
                        className="recipe-card-heading helper-section-header-link"
                        sx={helperSectionHeaderLinkSx}
                    >
                        {subData.heading ? parse(subData.heading) : ''}
                    </Typography>
                </Link>
            ) : (
                <Typography
                    variant="h3"
                    component="h3"
                    className="recipe-card-heading helper-section-header-link"
                    sx={helperSectionHeaderLinkSx}
                >
                    {subData.heading ? parse(subData.heading) : ''}
                </Typography>
            )}

            <CardContent className="helper-card-content-wrapper" sx={helperCardContentWrapperSx}>
                <Typography component="p" className="card-content data_links helper-section-card-content" sx={helperSectionCardContentSx}>
                    {subData.body ? parse(subData.body) : ''}
                </Typography>
            </CardContent>
        </Card>
    );
};

const HelpSection = (props: any) => {
    const {data, className, iconBoxSx} = props;
    return data?.map((subData: any, index: number) => (
        <Fragment key={index + 1}>
            <Grid item className="review-card-parent" xs={12} sm={6} md={4} lg={4}>
                <AnimatedComponent variants={fadeInUp} delay={index * 0.2} className="height-full">
                    {subData.redirect !== ''
                        ? renderCard(subData, className, '', iconBoxSx)
                        : renderCard(subData, className, 'non-link-box', iconBoxSx)}
                </AnimatedComponent>
            </Grid>
        </Fragment>
    ));
};

export default memo(HelpSection);
